(() => {
  if (window.GlowinexBundleV10?.mountAll) {
    window.GlowinexBundleV10.mountAll();
    return;
  }

  window.__glxBundleV10Loaded = true;
  window.__glxBundleV9Loaded = true;

  const PLACEMENT_SELECTOR = '[data-glx-bundle-v9]';
  const DRAWER_SELECTOR = '[data-glx-bundle-drawer="v10e"]';
  const cache = new Map();
  let instanceCounter = 0;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const currency = window.Shopify?.currency?.active || 'EUR';
  const rootPath = window.Shopify?.routes?.root || '/';
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const discountRate = (count) => ({ 2: 0.10, 3: 0.20, 4: 0.30 }[count] || 0);
  const discountTier = (count) => ({ 2: '10%', 3: '20%', 4: '30%' }[count] || '0%');

  // The sections bundled with /cart/add.js predate the subsequent discount
  // update. Always render a fresh, server-confirmed cart after BYL.sync().
  // Never overwrite its discount badge with stale pre-discount HTML.
  async function loadVerifiedCartSections(cart) {
    if (cart?.tagName !== 'CART-DRAWER' || typeof cart.getSectionsToRender !== 'function') return null;
    const ids = [...new Set(cart.getSectionsToRender().map(section => section.id).filter(Boolean))];
    if (!ids.length) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Unable to refresh the verified bundle cart.');
    const sections = await response.json();
    if (!sections || ids.some(id => typeof sections[id] !== 'string')) {
      throw new Error('The verified bundle cart sections are incomplete.');
    }
    return sections;
  }

  function controller() {
    return window.GlowinexBYLMarkerlessV3 || window.GlowinexBYLMarkerlessV2 || null;
  }

  function readCatalog(placement) {
    const source = placement.querySelector('[data-glx-bundle-catalog-v10]');
    let parsed = [];
    try {
      parsed = JSON.parse(source?.textContent || '[]');
    } catch (error) {
      console.warn('[Glowinex bundle v10e] Dynamic catalog JSON could not be parsed.', error);
    }

    const baseHandle = String(placement.dataset.productHandle || '').trim();
    const baseTitle = String(placement.dataset.productTitle || 'Glowinex product').trim();
    const deduped = new Map();

    for (const item of Array.isArray(parsed) ? parsed : []) {
      const handle = String(item?.handle || '').trim();
      const title = String(item?.title || '').trim();
      if (!handle) continue;
      deduped.set(handle, { title: title || handle, handle });
    }

    if (baseHandle && !deduped.has(baseHandle)) {
      deduped.set(baseHandle, { title: baseTitle || baseHandle, handle: baseHandle });
    }

    return Array.from(deduped.values());
  }

  async function loadProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const response = await fetch(`${rootPath}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to load product.');
    const product = await response.json();
    product.variants = (product.variants || []).filter((variant) => variant.available !== false);
    cache.set(handle, product);
    return product;
  }

  const findVariant = (product, id) => product?.variants?.find((variant) => String(variant.id) === String(id));
  const productOptions = (catalog, selectedHandle) => catalog.map((product) => (
    `<option value="${escapeHtml(product.handle)}"${product.handle === selectedHandle ? ' selected' : ''}>${escapeHtml(product.title)}</option>`
  )).join('');

  function createInstanceId(placement) {
    if (placement.dataset.glxBundleInstance) return placement.dataset.glxBundleInstance;
    instanceCounter += 1;
    const id = `glx-byl-${Date.now().toString(36)}-${instanceCounter.toString(36)}`;
    placement.dataset.glxBundleInstance = id;
    return id;
  }

  function closeOtherDrawers(currentRoot) {
    document.querySelectorAll(`${DRAWER_SELECTOR}.is-open`).forEach((drawer) => {
      if (drawer === currentRoot) return;
      if (typeof drawer.__glxClose === 'function') drawer.__glxClose();
      else drawer.classList.remove('is-open');
    });
  }

  function cleanupOrphanDrawers() {
    const activeInstances = new Set(
      Array.from(document.querySelectorAll(PLACEMENT_SELECTOR))
        .map((placement) => placement.dataset.glxBundleInstance)
        .filter(Boolean)
    );

    document.querySelectorAll(DRAWER_SELECTOR).forEach((drawer) => {
      const owner = drawer.dataset.glxBundleInstance;
      if (owner && !activeInstances.has(owner)) drawer.remove();
    });
  }

  function mount(placement) {
    if (!placement || placement.dataset.glxBundleMounted === 'true') return false;

    const catalog = readCatalog(placement);
    const baseHandle = placement.dataset.productHandle || '';
    const catalogBase = catalog.find((product) => product.handle === baseHandle);
    if (!catalogBase || catalog.length === 0) return false;

    const BASE_PRODUCT = {
      ...catalogBase,
      title: placement.dataset.productTitle || catalogBase.title,
    };

    const productForm = placement.closest('product-form');
    const form = productForm?.querySelector('form');
    const launch = placement.querySelector('#glx-bundle-launch-v9, [data-glx-bundle-launch-v10e]');
    if (!form || !launch) return false;

    placement.dataset.glxBundleMounted = 'true';
    const instanceId = createInstanceId(placement);

    const state = {
      count: 2,
      slots: Array.from({ length: 4 }, (_, index) => ({
        handle: BASE_PRODUCT.handle,
        variantId: null,
        fixedProduct: index === 0,
      })),
      busy: false,
      opener: null,
      previousOverflow: '',
    };

    const root = document.createElement('div');
    root.id = `glx-bundle-drawer-v10e-${instanceId}`;
    root.dataset.glxBundleDrawer = 'v10e';
    root.dataset.glxBundleInstance = instanceId;
    root.innerHTML = `
<div class="glx-bd" hidden></div>
<aside class="glx-p" role="dialog" aria-modal="true" aria-hidden="true" tabindex="-1">
<div class="glx-h">
<div>
<p class="glx-k">Bundle & save</p>
<h2 class="glx-t">Build your Glowinex setup</h2>
<p class="glx-sub">This product stays fixed. Choose its variant and add up to 3 more Glowinex lights.</p>
</div>
<button type="button" class="glx-x" aria-label="Close">×</button>
</div>
<div class="glx-b">
<div class="glx-count">
<button type="button" class="glx-c on" data-n="2"><strong>2 Items</strong><small>Save 10%</small></button>
<button type="button" class="glx-c" data-n="3"><strong>3 Items</strong><small>Save 20%</small></button>
<button type="button" class="glx-c" data-n="4"><strong>4 Items</strong><small>Save 30%</small></button>
</div>
<div class="glx-slots"></div>
<div class="glx-sum">
<div class="glx-row"><span>Subtotal</span><span data-s="sub">—</span></div>
<div class="glx-row glx-save"><span>Bundle saving</span><span data-s="save">—</span></div>
<div class="glx-row glx-total"><span>Bundle total</span><span data-s="total">—</span></div>
</div>
<p class="glx-err"></p>
</div>
<div class="glx-f">
<button type="button" class="glx-add" disabled>Add bundle to cart</button>
<p class="glx-note">2 items save 10% • 3 items save 20% • 4 items save 30% automatically.</p>
</div>
</aside>`;
    document.body.appendChild(root);

    const backdrop = root.querySelector('.glx-bd');
    const panel = root.querySelector('.glx-p');
    const slots = root.querySelector('.glx-slots');
    const addButton = root.querySelector('.glx-add');
    const errorText = root.querySelector('.glx-err');
    const closeButton = root.querySelector('.glx-x');
    const quickAddModal = placement.closest('quick-add-modal');
    const sum = {
      sub: root.querySelector('[data-s="sub"]'),
      save: root.querySelector('[data-s="save"]'),
      total: root.querySelector('[data-s="total"]'),
    };

    const currentVariantId = () => String(form.querySelector('[name="id"]')?.value || '');

    function pickedItems() {
      const items = [];
      for (let index = 0; index < state.count; index += 1) {
        const slot = state.slots[index];
        const product = cache.get(slot.handle);
        const variant = findVariant(product, slot.variantId);
        if (!variant) return [];
        items.push({ product, variant });
      }
      return items;
    }

    function updateSummary() {
      const items = pickedItems();
      if (items.length !== state.count) {
        sum.sub.textContent = '—';
        sum.save.textContent = '—';
        sum.total.textContent = '—';
        addButton.disabled = true;
        addButton.textContent = 'Add bundle to cart';
        return;
      }
      const subtotal = items.reduce((total, item) => total + Number(item.variant.price || 0), 0);
      const saving = Math.round(subtotal * discountRate(state.count));
      const total = subtotal - saving;
      sum.sub.textContent = money(subtotal);
      sum.save.textContent = `−${money(saving)}`;
      sum.total.textContent = money(total);
      addButton.disabled = state.busy;
      addButton.textContent = `Add bundle to cart — ${money(total)}`;
    }

    function paintCard(card, product, variant) {
      card.querySelector('.glx-price').textContent = money(variant.price);
      const image = card.querySelector('.glx-img');
      const imageUrl = variant.featured_image?.src || product.featured_image || product.images?.[0];
      if (imageUrl) {
        image.src = imageUrl;
        image.hidden = false;
      }
    }

    async function hydrateFixed(preferredVariantId) {
      const card = slots.querySelector('[data-i="0"]');
      if (!card) return;
      const slot = state.slots[0];
      const variantSelect = card.querySelector('[data-v-fixed]');
      const status = card.querySelector('.glx-status');
      slot.handle = BASE_PRODUCT.handle;
      slot.variantId = null;
      updateSummary();

      try {
        const product = await loadProduct(BASE_PRODUCT.handle);
        if (!card.isConnected) return;
        if (!product.variants.length) throw new Error('No available variants.');
        const selected = findVariant(product, preferredVariantId) || product.variants[0];
        slot.variantId = String(selected.id);
        variantSelect.innerHTML = product.variants.map((variant) => (
          `<option value="${variant.id}"${String(variant.id) === slot.variantId ? ' selected' : ''}>${escapeHtml(variant.title)} — ${escapeHtml(money(variant.price))}</option>`
        )).join('');
        variantSelect.disabled = false;
        paintCard(card, product, selected);
        status.textContent = 'Product fixed • Variant selectable';
        variantSelect.onchange = () => {
          slot.variantId = variantSelect.value;
          const variant = findVariant(product, slot.variantId);
          if (variant) paintCard(card, product, variant);
          updateSummary();
        };
        updateSummary();
      } catch (error) {
        console.error(error);
        variantSelect.innerHTML = '<option>Unavailable</option>';
        status.textContent = error.message || 'Could not load this product.';
        updateSummary();
      }
    }

    async function hydrateEditable(index, preferredVariantId) {
      const card = slots.querySelector(`[data-i="${index}"]`);
      const slot = state.slots[index];
      if (!card || !slot) return;
      const handle = slot.handle;
      const variantSelect = card.querySelector('[data-v]');
      const status = card.querySelector('.glx-status');
      variantSelect.disabled = true;
      variantSelect.innerHTML = '<option>Loading…</option>';
      slot.variantId = null;
      updateSummary();

      try {
        const product = await loadProduct(handle);
        if (state.slots[index].handle !== handle || !card.isConnected) return;
        card.querySelector('.glx-name').textContent =
          product.title || catalog.find((item) => item.handle === handle)?.title || 'Glowinex product';
        if (!product.variants.length) throw new Error('No available variants.');
        const selected = findVariant(product, preferredVariantId) || product.variants[0];
        slot.variantId = String(selected.id);
        variantSelect.innerHTML = product.variants.map((variant) => (
          `<option value="${variant.id}"${String(variant.id) === slot.variantId ? ' selected' : ''}>${escapeHtml(variant.title)} — ${escapeHtml(money(variant.price))}</option>`
        )).join('');
        variantSelect.disabled = false;
        paintCard(card, product, selected);
        status.textContent = 'Available';
        variantSelect.onchange = () => {
          slot.variantId = variantSelect.value;
          const variant = findVariant(product, slot.variantId);
          if (variant) paintCard(card, product, variant);
          updateSummary();
        };
        updateSummary();
      } catch (error) {
        console.error(error);
        variantSelect.innerHTML = '<option>Unavailable</option>';
        status.textContent = error.message || 'Could not load this product.';
        card.querySelector('.glx-price').textContent = '—';
        updateSummary();
      }
    }

    function render() {
      const preferredBaseVariant = state.slots[0].variantId || currentVariantId();
      const cards = [];
      cards.push(`<section class="glx-slot glx-slot-fixed" data-i="0">
<div class="glx-top"><img class="glx-img" alt="" hidden><div class="glx-meta"><small>Item 1 • Fixed product</small><div class="glx-name">${escapeHtml(BASE_PRODUCT.title)}</div></div><div class="glx-price">—</div></div>
<div class="glx-fixed-label">Current product • Fixed</div>
<label class="glx-field"><span>Variant</span><select data-v-fixed disabled><option>Loading…</option></select></label>
<div class="glx-status">Loading available variants…</div>
</section>`);

      for (let index = 1; index < state.count; index += 1) {
        const slot = state.slots[index];
        cards.push(`<section class="glx-slot" data-i="${index}">
<div class="glx-top"><img class="glx-img" alt="" hidden><div class="glx-meta"><small>Item ${index + 1}</small><div class="glx-name">Glowinex product</div></div><div class="glx-price">—</div></div>
<label class="glx-field"><span>Product</span><select data-p>${productOptions(catalog, slot.handle)}</select></label>
<label class="glx-field"><span>Variant</span><select data-v disabled><option>Loading…</option></select></label>
<div class="glx-status">Loading availability…</div>
</section>`);
      }

      slots.innerHTML = cards.join('');
      hydrateFixed(preferredBaseVariant);
      for (let index = 1; index < state.count; index += 1) {
        const card = slots.querySelector(`[data-i="${index}"]`);
        const productSelect = card.querySelector('[data-p]');
        const previousVariantId = state.slots[index].variantId;
        productSelect.onchange = () => {
          state.slots[index].handle = productSelect.value;
          state.slots[index].variantId = null;
          hydrateEditable(index, null);
        };
        hydrateEditable(index, previousVariantId);
      }
    }

    function openDrawer() {
      closeOtherDrawers(root);
      state.opener = document.activeElement;
      state.slots[0].handle = BASE_PRODUCT.handle;
      state.slots[0].variantId = currentVariantId();
      errorText.textContent = '';
      render();
      root.classList.add('is-open');
      backdrop.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      state.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      closeButton.focus();
    }

    function closeDrawer() {
      root.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = state.previousOverflow;
      window.setTimeout(() => {
        backdrop.hidden = true;
        state.opener?.focus?.();
      }, 260);
    }

    root.__glxClose = closeDrawer;
    launch.onclick = openDrawer;
    closeButton.onclick = closeDrawer;
    backdrop.onclick = closeDrawer;

    root.querySelectorAll('.glx-c').forEach((button) => {
      button.onclick = () => {
        state.count = Math.max(2, Math.min(4, Number(button.dataset.n)));
        root.querySelectorAll('.glx-c').forEach((item) => item.classList.toggle('on', item === button));
        errorText.textContent = '';
        render();
      };
    });

    panel.onkeydown = (event) => {
      if (event.key === 'Escape') closeDrawer();
    };

    addButton.onclick = async () => {
      if (state.busy) return;
      const items = pickedItems();
      if (items.length !== state.count) {
        errorText.textContent = 'Please choose an available product and variant for every item.';
        return;
      }

      state.busy = true;
      errorText.textContent = '';
      updateSummary();

      try {
        const byl = controller();
        if (!byl?.prepareForBundle) {
          throw new Error('Build Your Light cart controller is unavailable. Please refresh and try again.');
        }
        await byl.prepareForBundle();

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const data = new FormData();
        items.forEach((item, index) => {
          data.append(`items[${index}][id]`, String(item.variant.id));
          data.append(`items[${index}][quantity]`, '1');
          data.append(`items[${index}][properties][_Glowinex Bundle]`, 'Yes');
          data.append(`items[${index}][properties][_Bundle Size]`, String(state.count));
          data.append(`items[${index}][properties][_Bundle Discount Tier]`, discountTier(state.count));
          data.append(`items[${index}][properties][_Bundle Item]`, String(index + 1));
          data.append(`items[${index}][properties][_Bundle Version]`, 'v10-dynamic');
        });

        if (cart?.getSectionsToRender) {
          data.append('sections', cart.getSectionsToRender().map((section) => section.id));
          data.append('sections_url', window.location.pathname);
        }

        const config = typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/json' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = data;

        const addUrl = window.routes?.cart_add_url || `${rootPath}cart/add.js`;
        const response = await fetch(addUrl, config);
        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Unable to add bundle.');
        }

        let verifiedState = null;
        try {
          verifiedState = await byl.sync?.({ strict: true, fresh: true });
        } catch (syncError) {
          console.warn('[Glowinex bundle v10e] Bundle pricing verification failed.', syncError);
        }

        if (!verifiedState?.eligible) {
          const clearUrl = window.routes?.cart_clear_url || `${rootPath}cart/clear.js`;
          try {
            await fetch(clearUrl, {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
            });
            byl.scheduleSync?.(0);
          } catch (rollbackError) {
            console.warn('[Glowinex bundle v10e] Bundle rollback failed.', rollbackError);
          }
          throw new Error('Bundle pricing could not be verified. The bundle was not kept in your cart. Please try again.');
        }

        let verifiedSections = null;
        try {
          verifiedSections = await loadVerifiedCartSections(cart);
        } catch (sectionError) {
          console.warn('[Glowinex bundle v10e] Fresh bundle cart rendering failed.', sectionError);
        }

        closeDrawer();
        if (quickAddModal && typeof quickAddModal.hide === 'function') quickAddModal.hide();

        if (cart?.renderContents && verifiedSections) {
          cart.renderContents({ sections: verifiedSections });
        } else {
          // Safe fallback: show Shopify's current cart instead of stale,
          // pre-discount /cart/add.js HTML. Checkout remains untouched.
          window.location.href = window.routes?.cart_url || `${rootPath}cart`;
        }
      } catch (error) {
        console.error(error);
        errorText.textContent = error.message || 'Unable to add bundle.';
      } finally {
        state.busy = false;
        updateSummary();
      }
    };

    return true;
  }

  function mountAll() {
    cleanupOrphanDrawers();
    let mounted = 0;
    document.querySelectorAll(PLACEMENT_SELECTOR).forEach((placement) => {
      if (placement.dataset.glxBundleMounted === 'true') return;
      if (mount(placement)) mounted += 1;
    });
    return mounted;
  }

  function boot() {
    mountAll();
    const observer = new MutationObserver(() => mountAll());
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.GlowinexBundleV10 = { mountAll, version: 'v10e3' };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();